import { FieldServiceContract, FieldServiceListItem, ScheduleStoreAssignment } from '@Models';

interface ScheduleEnrichment {
  storeCount: number;
  retailers: string[];
  firmIds: string[];
  firmNames: string[];
}

const EMPTY_ENRICHMENT: ScheduleEnrichment = { storeCount: 0, retailers: [], firmIds: [], firmNames: [] };

const buildScheduleEnrichment = (
  assignments: ScheduleStoreAssignment[],
  contracts: FieldServiceContract[],
  fieldServices: FieldServiceListItem[],
): Record<string, ScheduleEnrichment> => {
  const firmNameByFieldServiceId = new Map<string, string>();
  fieldServices.forEach((fieldService) => firmNameByFieldServiceId.set(fieldService.id, fieldService.name));

  const fieldServiceIdByContractId = new Map<string, string>();
  contracts.forEach((contract) => fieldServiceIdByContractId.set(contract.id, contract.field_service_id));

  const enrichmentById: Record<string, ScheduleEnrichment> = {};
  const retailerCountsByScheduleId: Record<string, Record<string, number>> = {};

  assignments.forEach((assignment) => {
    const entry = enrichmentById[assignment.schedule_id] ?? {
      storeCount: 0,
      retailers: [],
      firmIds: [],
      firmNames: [],
    };
    entry.storeCount += 1;
    if (!entry.retailers.includes(assignment.retailer)) {
      entry.retailers.push(assignment.retailer);
    }
    retailerCountsByScheduleId[assignment.schedule_id] ??= {};
    const retailerCounts = retailerCountsByScheduleId[assignment.schedule_id];
    retailerCounts[assignment.retailer] = (retailerCounts[assignment.retailer] ?? 0) + 1;

    if (assignment.contract_id) {
      const fieldServiceId = fieldServiceIdByContractId.get(assignment.contract_id);
      const firmName = fieldServiceId ? firmNameByFieldServiceId.get(fieldServiceId) : undefined;
      if (fieldServiceId && firmName && !entry.firmIds.includes(fieldServiceId)) {
        entry.firmIds.push(fieldServiceId);
        entry.firmNames.push(firmName);
      }
    }
    enrichmentById[assignment.schedule_id] = entry;
  });

  Object.entries(enrichmentById).forEach(([scheduleId, entry]) => {
    const retailerCounts = retailerCountsByScheduleId[scheduleId] ?? {};
    entry.retailers.sort((a, b) => (retailerCounts[b] ?? 0) - (retailerCounts[a] ?? 0));
  });

  return enrichmentById;
};

const getScheduleEnrichment = (
  enrichmentById: Record<string, ScheduleEnrichment>,
  scheduleId: string,
): ScheduleEnrichment => enrichmentById[scheduleId] ?? EMPTY_ENRICHMENT;

export { buildScheduleEnrichment, getScheduleEnrichment };
export type { ScheduleEnrichment };
